import '../style.scss'

import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Select } from '~/components/controls'
import { RegistrationModalWrapper } from '~/components/modals'
import { PageMeta } from '~/components/PageMeta'
import { AirportStructuredData, BreadcrumbStructuredData } from '~/components/StructuredData'
import DestinationText from '~/components/UI/DestinationText/DestinationText'
import { NOINDEX_FOLLOW_ROBOTS, PageKey } from '~/constants/pageMeta'
import { getAirportCountrySlug } from '~/helpers/airport-directory'
import {
    buildAirportDescription,
    buildAirportPageTitle,
    formatAirportLocation,
    formatAirportTitle,
    formatRunwayDimensionsMeters,
    getPublishedAirportRunways,
    isAirportIndexable,
    normalizeAirportText,
    normalizeExternalUrl,
} from '~/helpers/airport-seo'
import { createUrlSlug } from '~/helpers/url-slug'
import { GOOGLE_EVENTS, sendGoogleGoal } from '~/libs/analytic/ga'
import { trackPublicCatalogLink } from '~/libs/analytic/public-catalog'
import { openFormRegistration } from '~/routes/main/redux/rtk/mainSlice'

import LeftIcon from '../../../assets/images/icons/arrow-left.svg?react'
import { SiteFooter } from '../../../components/site-footer'
import { SiteHeader } from '../../../components/site-header'
import { CompanyModel } from '../../../db/airports/model/CompanyModel'
import { type AirportDetails, airportsListSelector } from '../rtk/store/airportSlice'

const formatCoordinates = (airport: AirportDetails | null) => [airport?.latitude, airport?.longitude]
    .filter((value) => value !== undefined && value !== null)
    .join(', ')

const formatSourceDate = (value: string) => {
    if (!value) return ''

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''

    return new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
        year: 'numeric',
    }).format(date)
}

const AIRPORT_SOURCE_NAMES: Record<string, string> = {
    'faa-nasr': 'FAA Airport Data and Information Portal',
    ourairports: 'OurAirports',
}

const formatSourceName = (value: string) => AIRPORT_SOURCE_NAMES[value.toLowerCase()] || value

const Page: FC = () => {
    const dispatch = useDispatch()
    const openRegistration = () => { dispatch(openFormRegistration()) }
    const airport = useSelector(airportsListSelector)
    const companies = airport?.companies || []
    const runways = getPublishedAirportRunways(airport)
    const nearestAirports = airport?.nearestAirports || []
    const compatibleAircraftModels = airport?.compatibleAircraftModels || []
    const dataSources = (airport?.data_sources || []).filter((source) => (
        source.source_name || normalizeExternalUrl(source.source_url)
    ))

    const [selectedCompany, setSelectedCompany] = useState<CompanyModel | null>(companies[0] || null)

    useEffect(() => {
        setSelectedCompany(companies[0] || null)
    }, [companies])

    const infoProperties = [
        { label: 'Phone:', value: airport?.phone },
        { label: 'Site:', value: airport?.website, isLink: true },
        { label: 'Email:', value: airport?.email, isEmail: true },
    ].filter((prop) => prop.value)

    const locationProperties = [
        { label: 'Location:', value: formatCoordinates(airport) },
        { label: 'PCN:', value: airport?.pcn },
        { label: 'Elevation:', value: airport?.runway_elevation ? `${airport.runway_elevation} m` : '' },
    ].filter((prop) => prop.value)

    const handlersContactProperties = selectedCompany
        ? [
            { label: 'Phone:', value: selectedCompany.phone },
            { label: 'Site:', value: selectedCompany.website, isLink: true },
            { label: 'Email:', value: selectedCompany.contact_email, isEmail: true },
        ].filter((prop) => prop.value)
        : []

    const handlersElevationProperties = selectedCompany
        ? [
            { label: 'MRO', flag: selectedCompany.is_mro },
            { label: 'FBO Services', flag: selectedCompany.is_fbo },
            { label: 'Inflight Catering', flag: selectedCompany.is_catering },
            { label: 'Aircraft Fuel Arrangements', flag: selectedCompany.is_fuel_supply },
            { label: 'Aircraft Sales', flag: selectedCompany.is_aircraft_dealer },
            { label: 'Operator', flag: selectedCompany.is_operator },
            { label: 'Manufacturer', flag: selectedCompany.is_manufacturer },
            { label: 'Broker', flag: selectedCompany.is_broker },
            { label: 'Ground Handling', flag: selectedCompany.is_handling },
            { label: 'Flight Support', flag: selectedCompany.is_flight_support },
            { label: 'Overflight & Landing Permits', flag: selectedCompany.is_overflight_landing_permits },
            { label: 'Airport Slots', flag: selectedCompany.is_airport_slots },
            { label: 'Aircraft Parking', flag: selectedCompany.is_aircraft_parking },
            { label: 'Crew & Passenger Services', flag: selectedCompany.is_crew_passenger_services },
            { label: 'Hotel Accommodation', flag: selectedCompany.is_hotel_accommodation },
            { label: 'Crew Transportation', flag: selectedCompany.is_crew_transportation },
            { label: 'VIP Concierge Services', flag: selectedCompany.is_vip_concierge_services },
            { label: 'Aircraft Charter', flag: selectedCompany.is_aircraft_charter },
            { label: 'Trip Planning', flag: selectedCompany.is_trip_planning },
            { label: 'Weather Briefing', flag: selectedCompany.is_weather_briefing },
            { label: 'Flight Planning', flag: selectedCompany.is_flight_planning },
            { label: 'Customs & Immigration Coordination', flag: selectedCompany.is_customs_immigration_coordination },
            { label: 'Aircraft Security Arrangements', flag: selectedCompany.is_aircraft_security_arrangements },
        ].filter((prop) => prop.flag)
        : []

    const airportTitle = formatAirportTitle(airport)
    const airportIataCode = normalizeAirportText(airport?.iata_code).toUpperCase()
    const airportIcaoCode = normalizeAirportText(airport?.icao_code).toUpperCase()
    const airportName = normalizeAirportText(airport?.name)
    const hasAirportCode = Boolean(airportIataCode || airportIcaoCode)
    const airportSubtitle = formatAirportLocation(airport)
    const title = buildAirportPageTitle(airport)
    const pageDescription = buildAirportDescription(airport)
    const overviewDetails = [
        runways.length ? `${runways.length} runway record${runways.length === 1 ? '' : 's'}` : null,
        companies.length ? `${companies.length} ground handling provider${companies.length === 1 ? '' : 's'}` : null,
        airport?.phone || airport?.email || airport?.website ? 'direct airport contact details' : null,
    ].filter(Boolean)
    const overviewSummary = overviewDetails.length
        ? overviewDetails.join(', ').replace(/, ([^,]*)$/, ' and $1')
        : 'available airport information'
    const overviewText = [
        `${airportTitle}${airportSubtitle ? ` is located in ${airportSubtitle}` : ''}.`,
        `This page includes ${overviewSummary}.`,
    ].join(' ')
    const canonicalPath = airport?.icao_code
        ? `/airport/${String(airport.icao_code).toLowerCase()}`
        : undefined
    const shouldIndexAirport = isAirportIndexable(airport)
    const canonicalUrl = canonicalPath ? `https://worldtransair.com${canonicalPath}` : ''
    const countrySlug = getAirportCountrySlug(airport?.country || '')
    const countryUrl = countrySlug ? `https://worldtransair.com/airports/${countrySlug}` : ''
    const charterLocation = [
        normalizeAirportText(airport?.city) || airportName,
        airportIataCode || airportIcaoCode,
    ].filter(Boolean).join(' ')
    const breadcrumbItems = [
        { name: 'World Trans Air', url: 'https://worldtransair.com/' },
        { name: 'Airport directory', url: 'https://worldtransair.com/airports' },
        ...(countryUrl && airport ? [{ name: airport.country, url: countryUrl }] : []),
        { name: airportTitle, url: canonicalUrl },
    ]

    return (
        <>
            <PageMeta
                pageKey={PageKey.AIRPORT_DETAILS}
                title={title}
                description={pageDescription}
                canonicalPath={canonicalPath}
                robots={shouldIndexAirport ? undefined : NOINDEX_FOLLOW_ROBOTS}
            />
            {canonicalUrl && airport && (
                <>
                    <BreadcrumbStructuredData items={breadcrumbItems} />
                    <AirportStructuredData airport={airport} url={canonicalUrl} />
                </>
            )}
            <div className="airports grey-block">
                <SiteHeader onOpenRegistration={openRegistration} />
                <main
                    className="workspace-container airport-card"
                    onClickCapture={(event) => trackPublicCatalogLink(event, 'airport', airportIcaoCode)}
                >
                    <a className="airport-card__back-button" href="/airports">
                        <LeftIcon width="20" />
                        {' '}
                        Back
                    </a>
                    <h1 className="airport-card__title" aria-label={airportTitle}>
                        {hasAirportCode && (
                            <span className="airport-card__title-code">
                                <DestinationText
                                    locationIata={airportIataCode}
                                    locationIcao={airportIcaoCode}
                                />
                            </span>
                        )}
                        {hasAirportCode && airportName && (
                            <span className="airport-card__title-separator" aria-hidden="true">•</span>
                        )}
                        {airportName && <span>{airportName}</span>}
                    </h1>
                    <div className="airport-card__subtitle">{airportSubtitle}</div>
                    <div className="white-block white-block_padding">
                        <section className="airport-card__overview">
                            <h2 className="airport-card__overview-title">Airport information</h2>
                            <p className="airport-card__overview-text">
                                {overviewText}
                            </p>
                            {countrySlug && (
                                <a className="airport-card__country-link" href={`/airports/${countrySlug}`}>
                                    {`All airports in ${airport?.country}`}
                                </a>
                            )}
                        </section>
                        {charterLocation && (
                            <section className="airport-card__charter" aria-label={`Cargo charter from ${charterLocation}`}>
                                <div>
                                    <h2>{`Cargo charter from ${charterLocation}`}</h2>
                                    <p>Send your cargo details to verified operators or review published empty-leg capacity.</p>
                                </div>
                                <div className="airport-card__charter-actions">
                                    <button
                                        type="button"
                                        className="button button_blue airport-card__charter-primary"
                                        onClick={() => {
                                            sendGoogleGoal(GOOGLE_EVENTS.airport_request_charter, { airport: airportIcaoCode })
                                            openRegistration()
                                        }}
                                    >
                                        {`Request a cargo charter from ${charterLocation}`}
                                    </button>
                                    <a className="button airport-card__charter-secondary" href="/public-empty-legs">
                                        View empty legs
                                    </a>
                                </div>
                            </section>
                        )}
                        <div className="airport-card__section">
                            <div className="airport-card__section-wrapper">
                                <div className="airport-card__left">
                                    <div className="airport-card__label">Info</div>
                                </div>
                                <div className="airport-card__right">
                                    {infoProperties.length > 0 && (
                                        <div className="airport-card__info-column">
                                            {infoProperties.map((prop) => (
                                                <div key={prop.label} className="airport-card__info-row">
                                                    <span className="airport-card__label-item">{prop.label}</span>
                                                    {(() => {
                                                        if (prop.isLink) {
                                                            const externalUrl = normalizeExternalUrl(prop.value)
                                                            if (!externalUrl) return <span className="airport-card__value">{prop.value}</span>

                                                            return (
                                                                <a
                                                                    href={externalUrl}
                                                                    target="_blank"
                                                                    className="airport-card__value airport-card__link"
                                                                    rel="noreferrer"
                                                                >
                                                                    {prop.value}
                                                                </a>
                                                            )
                                                        }
                                                        if (prop.isEmail) {
                                                            return (
                                                                <a href={`mailto:${prop.value}`} className="airport-card__value airport-card__link">
                                                                    {prop.value}
                                                                </a>
                                                            )
                                                        }
                                                        return <span className="airport-card__value">{prop.value}</span>
                                                    })()}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {locationProperties.length > 0 && (
                                        <div className="airport-card__info-column">
                                            {locationProperties.map((prop) => (
                                                <div key={prop.label} className="airport-card__info-row">
                                                    <span className="airport-card__label-item">{prop.label}</span>
                                                    <span className="airport-card__value">{prop.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="airport-card__runway">
                                        <h2 className="airport-card__runway-title">Runways</h2>
                                        <div className="airport-card__runway-row airport-card__runway-row_header">
                                            <div className="airport-card__runway-item">
                                                <span className="airport-card__label-item">Name</span>
                                            </div>
                                            <div className="airport-card__runway-item">
                                                <span className="airport-card__label-item">Type</span>
                                            </div>
                                            <div className="airport-card__runway-item">
                                                <span className="airport-card__label-item">Lights</span>
                                            </div>
                                            <div className="airport-card__runway-item">
                                                <span className="airport-card__label-item">Length</span>
                                            </div>
                                        </div>
                                        {runways.length > 0 ? (
                                            runways.map((runway) => (
                                                <div key={runway.id} className="airport-card__runway-row">
                                                    <div className="airport-card__runway-item">
                                                        <span className="airport-card__runway-mobile-label">Name</span>
                                                        <span className="airport-card__value">{runway.name}</span>
                                                    </div>
                                                    <div className="airport-card__runway-item">
                                                        <span className="airport-card__runway-mobile-label">Type</span>
                                                        <span className="airport-card__value">{runway.type}</span>
                                                    </div>
                                                    <div className="airport-card__runway-item">
                                                        <span className="airport-card__runway-mobile-label">Lights</span>
                                                        <span className="airport-card__value">{runway.lights}</span>
                                                    </div>
                                                    <div className="airport-card__runway-item">
                                                        <span className="airport-card__runway-mobile-label">Length</span>
                                                        <span className="airport-card__value">
                                                            {formatRunwayDimensionsMeters(runway)}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="airport-card__runway-row">
                                                <div className="airport-card__runway-item">
                                                    <span className="airport-card__value">No runway data available</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {selectedCompany && (
                            <div className="airport-card__section">
                                <div className="airport-card__section-wrapper">
                                    <div className="airport-card__left">
                                        <div className="airport-card__label">Handlers</div>
                                        <Select
                                            placeholder="Select company"
                                            modClass="airport-card__company"
                                            options={companies.map((company: CompanyModel) => ({
                                                label: company.company_name,
                                                value: company.id,
                                            }))}
                                            selectedValue={selectedCompany?.id || companies[0]?.id}
                                            onChange={(value: string | number) => {
                                                sendGoogleGoal(GOOGLE_EVENTS.airport_handler_select, { airport: airportIcaoCode, handler_id: value })
                                                const company = companies.find((c: CompanyModel) => c.id === value)
                                                setSelectedCompany(company || null)
                                            }}
                                            isSearchable={false}
                                            withBorder
                                        />
                                    </div>
                                    <div className="airport-card__right airport-card__right_handlers">
                                        <h2 className="airport-card__section-title">{selectedCompany.company_name}</h2>
                                        {handlersContactProperties.length > 0 && (
                                            <div className="airport-card__info-column">
                                                <h3 className="airport-card__info-column-title">Contact</h3>
                                                {handlersContactProperties.map((prop) => (
                                                    <div key={prop.label} className="airport-card__info-row">
                                                        <span className="airport-card__label-item">{prop.label}</span>
                                                        {(() => {
                                                            if (prop.isLink) {
                                                                const externalUrl = normalizeExternalUrl(prop.value)
                                                                if (!externalUrl) return <span className="airport-card__value">{prop.value}</span>

                                                                return (
                                                                    <a
                                                                        href={externalUrl}
                                                                        target="_blank"
                                                                        className="airport-card__value airport-card__link"
                                                                        rel="noreferrer"
                                                                    >
                                                                        {prop.value}
                                                                    </a>
                                                                )
                                                            }
                                                            if (prop.isEmail) {
                                                                return (
                                                                    <a href={`mailto:${prop.value}`} className="airport-card__value airport-card__link">
                                                                        {prop.value}
                                                                    </a>
                                                                )
                                                            }
                                                            return <span className="airport-card__value">{prop.value}</span>
                                                        })()}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {handlersElevationProperties.length > 0 && (
                                            <div className="airport-card__info-column">
                                                <h3 className="airport-card__info-column-title">Services</h3>
                                                {handlersElevationProperties.map((prop) => (
                                                    <div key={prop.label} className="airport-card__info-row">
                                                        <span className="airport-card__label-item">{prop.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {compatibleAircraftModels.length > 0 && (
                            <section className="airport-card__related" aria-label="Compatible aircraft models">
                                <div className="airport-card__related-group">
                                    <h2>Compatible aircraft models</h2>
                                    <p>
                                        Verified against published aircraft requirements and this airport&apos;s runway data.
                                    </p>
                                    <ul>
                                        {compatibleAircraftModels.map((model) => {
                                            const displayName = model.shortName || model.model
                                            const detail = [
                                                displayName !== model.model ? model.model : '',
                                                `Runway ${model.runwayDirection}`,
                                            ].filter(Boolean).join(' · ')

                                            return (
                                                <li key={model.id}>
                                                    <a href={`/models/${createUrlSlug(model.model)}`}>
                                                        <strong>{displayName}</strong>
                                                        <span>{detail}</span>
                                                    </a>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            </section>
                        )}

                        {nearestAirports.length > 0 && (
                            <section className="airport-card__related" aria-label="Related planning references">
                                <div className="airport-card__related-group">
                                    <h2>Nearest airports</h2>
                                    <p>Distances are calculated from published airport coordinates.</p>
                                    <ul>
                                        {nearestAirports.map((nearbyAirport) => (
                                            <li key={nearbyAirport.id}>
                                                <a href={`/airport/${nearbyAirport.icao_code.toLowerCase()}`}>
                                                    <strong>{formatAirportTitle(nearbyAirport)}</strong>
                                                    <span>
                                                        {`${formatAirportLocation(nearbyAirport)} · ${Math.round(nearbyAirport.distance_km)} km`}
                                                    </span>
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        )}

                        {dataSources.length > 0 && (
                            <section className="airport-card__related" aria-label="Airport data sources">
                                <div className="airport-card__related-group">
                                    <h2>Data sources</h2>
                                    <ul>
                                        {dataSources.map((source) => {
                                            const sourceUrl = normalizeExternalUrl(source.source_url)
                                            const sourceDate = formatSourceDate(source.source_updated_at)
                                            const details = [
                                                source.source_tier ? `${source.source_tier[0].toUpperCase()}${source.source_tier.slice(1)}` : '',
                                                sourceDate ? `Updated ${sourceDate}` : '',
                                            ].filter(Boolean).join(' · ')
                                            const content = (
                                                <>
                                                    <strong>
                                                        {source.source_name
                                                            ? formatSourceName(source.source_name)
                                                            : new URL(sourceUrl).hostname.replace(/^www\./, '')}
                                                    </strong>
                                                    {details && <span>{details}</span>}
                                                </>
                                            )

                                            return (
                                                <li key={`${source.source_name}-${source.source_record_id}`}>
                                                    {sourceUrl ? (
                                                        <a href={sourceUrl} target="_blank" rel="noreferrer">{content}</a>
                                                    ) : (
                                                        <div className="airport-card__source-item">{content}</div>
                                                    )}
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </div>
                            </section>
                        )}
                    </div>
                </main>

                <SiteFooter />
                <RegistrationModalWrapper />
            </div>
        </>
    )
}

export default Page
